/**
 * @file Main Photo Grid component.
 *
 * @description High level component responsible for displaying large sets
 * of photo records in a justified grid using windowing (with React Virtualized).
 */

import React, { Component, createContext, Fragment } from 'react';
import PropTypes from 'prop-types';
import { WindowScroller, AutoSizer, Collection } from 'react-virtualized';
import justifiedLayout from 'justified-layout';
import memoize from 'fast-memoize';
import get from 'lodash.get';
import { Route, withRouter } from 'react-router-dom';
import qs from 'query-string';

import { getImageRatioFromThumbnails } from '../../util/photo';
import PhotoGridScrollLoader from './components/PhotoGridScrollLoader';
import PhotoGridViewer from './components/PhotoGridViewer';

/**
 * Provides a ref scroll ref to the photoGrid.
 */
const PhotoGridScrollContext = createContext(null);

export { PhotoGridScrollContext };

class PhotoGrid extends Component {
  static propTypes = {
    loading: PropTypes.bool,
    records: PropTypes.arrayOf(PropTypes.object),
    total: PropTypes.number,
    loadMore: PropTypes.func,
    cellRenderer: PropTypes.func.isRequired,
    children: PropTypes.func,
    loadingComponent: PropTypes.node,
    recordPhotoAccessor: PropTypes.string,
    // This width is used for SSR and is irrelevant as when the
    // component mounts will auto-update to the correct dimensions
    // given the existance of the real DOM nodes.
    defaultContainerWidth: PropTypes.number,
    defaultRecordRatio: PropTypes.number,
    forcedRecordRatio: PropTypes.number,
    targetRowHeight: PropTypes.number,
    windowScrollerProps: PropTypes.object,
    justifiedLayoutOptions: PropTypes.shape({
      containerWidth: PropTypes.number,
      containerPadding: PropTypes.number,
      boxSpacing: PropTypes.number,
      targetRowHeightTolerance: PropTypes.number,
      maxNumRows: PropTypes.number,
      forceAspectRatio: PropTypes.bool,
      showWidows: PropTypes.bool,
      fullWidthBreakoutRowCadence: PropTypes.bool,
      widowLayoutStyle: PropTypes.oneOf(['justify', 'center', 'left']),
    }),
    match: PropTypes.shape({
      path: PropTypes.string,
    }),
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
    nativePhotoViewer: PropTypes.bool,
    photoViewerProps: PropTypes.shape({
      actions: PropTypes.func,
      footer: PropTypes.func,
      imageSizeCode: PropTypes.string,
      renderPhoto: PropTypes.func,
    }),
    getShouldLoadMore: PropTypes.func,
  };

  static defaultProps = {
    records: [],
    children: () => {},
    defaultContainerWidth: 1060,
    defaultRecordRatio: 1,
    targetRowHeight: 280,
    windowScrollerProps: {},
    photoViewerProps: {},
  };

  state = {
    targetPhotoFound: false,
  };

  onGridResize = () => {
    if (this.collection) {
      this.collection.recomputeCellSizesAndPositions();
    }
  };

  onSectionRendered = ({ indices }) => {
    this.indicesRendered = indices;
  };

  // `secondaryCache` here just so that memoization happens
  // based on last 10 record Ids & the records length instead of only `width` as
  // we need this function to recompute all box data upon
  // loading new records

  // Moved on from using records.length as secondary cache
  // as it would not update box data when the count is the same
  // but contents have changed.
  //
  // eslint-disable-next-line no-unused-vars
  getBoxData = memoize((containerWidth, secondaryCache) => {
    const {
      records,
      recordPhotoAccessor,
      defaultContainerWidth,
      defaultRecordRatio,
      forcedRecordRatio,
      targetRowHeight,
      justifiedLayoutOptions,
    } = this.props;
    // Using reduce here and not map+filter as we want
    // to exclude records without ratios/thumbnails in one pass
    const ratios = records.reduce((acc, record) => {
      const photo = recordPhotoAccessor
        ? get(record, recordPhotoAccessor)
        : record;

      const ratio =
        forcedRecordRatio ||
        getImageRatioFromThumbnails(photo.thumbnail_urls) ||
        defaultRecordRatio;

      acc.push(ratio);
      return acc;
    }, []);

    return justifiedLayout(ratios, {
      containerWidth: containerWidth || defaultContainerWidth,
      targetRowHeight,
      targetRowHeightTolerance: 0.175,
      boxSpacing: 2,
      containerPadding: 0,
      ...justifiedLayoutOptions,
    });
  });

  componentDidUpdate() {
    const { location, records, recordPhotoAccessor, loading } = this.props;
    const { targetPhotoFound } = this.state;
    const { targetPhotoId } = qs.parse(location?.search ?? '');
    const targetRecordIndex = (records ?? []).findIndex(
      record =>
        (record.id || record?.[recordPhotoAccessor]?.id) === targetPhotoId,
    );

    if (
      loading ||
      !targetPhotoId ||
      targetPhotoFound ||
      !targetRecordIndex ||
      !this.scroller?.props?.scrollElement ||
      // eslint-disable-next-line no-underscore-dangle
      !this.collection?._cellMetadata
    ) {
      return;
    }

    const { _cellMetadata: cells } = this.collection;
    this.scroller.props.scrollElement.scrollTo(0, cells[targetRecordIndex].y);
    // eslint-disable-next-line react/no-did-update-set-state
    this.setState(() => ({ targetPhotoFound: true }));
  }

  render() {
    const {
      loading,
      records,
      total,
      loadMore,
      cellRenderer,
      children,
      loadingComponent,
      defaultContainerWidth,
      windowScrollerProps,
      targetRowHeight,
      recordPhotoAccessor,
      match: { path, url },
      location,
      nativePhotoViewer,
      photoViewerProps,
      getShouldLoadMore,
    } = this.props;

    if (loading && !records?.length) {
      return loadingComponent || null;
    }

    const memoizationKey = [...records.slice(-10), ...records.slice(10)].reduce(
      (acc, item) => (item.id || item?.[recordPhotoAccessor]?.id) + acc,
      `${records.length}-${targetRowHeight}`,
    );
    const shouldLoadMore = getShouldLoadMore
      ? getShouldLoadMore()
      : total > records.length;

    return (
      <Fragment>
        {
          // Only here so that we can enable it per photo viewer.
          // When all photo viewers are replaced this check should be removed.
          nativePhotoViewer && (
            <Route
              path={`${path}/v/:recordId`}
              exact
              render={({ match }) => (
                <PhotoGridViewer
                  recordId={match.params.recordId}
                  records={records}
                  loadMore={loadMore}
                  shouldLoadMore={shouldLoadMore}
                  recordPhotoAccessor={recordPhotoAccessor}
                  gridPath={url}
                  {...photoViewerProps}
                />
              )}
            />
          )
        }

        <PhotoGridScrollContext.Consumer>
          {mainContainerRef => (
            <WindowScroller
              // mainContainerRef.current might be null, which breaks the photoGrid.
              // We need to pass undefined as an alternative just in case.
              scrollElement={mainContainerRef?.current || undefined}
              ref={ref => {
                this.scroller = ref;
              }}
              {...windowScrollerProps}
            >
              {({
                height, // Viewport height
                isScrolling,
                scrollTop,
                registerChild,
              }) => {
                return (
                  <AutoSizer
                    disableHeight
                    onResize={this.onGridResize}
                    height={height}
                    defaultWidth={defaultContainerWidth}
                  >
                    {({ width }) => {
                      const { boxes } = this.getBoxData(width, memoizationKey);

                      return (
                        <div
                          // Hack that works when rendering multiple WindowScrollers:
                          // https://github.com/bvaughn/react-virtualized/issues/1324#issuecomment-477989552
                          ref={el => registerChild(el)}
                          style={{ width: `${width}px` }}
                        >
                          <Collection
                            ref={ref => {
                              this.collection = ref;
                            }}
                            width={width}
                            height={height}
                            autoHeight
                            scrollTop={scrollTop}
                            className="photo-grid"
                            onSectionRendered={this.onSectionRendered}
                            cellCount={boxes.length}
                            verticalOverscanSize={height * 2}
                            cellSizeAndPositionGetter={({ index }) => ({
                              height: boxes[index].height,
                              width: boxes[index].width,
                              x: boxes[index].left,
                              y: boxes[index].top,
                            })}
                            cellRenderer={({ index, key, style }) => {
                              const record = records[index];
                              const recordPath = `${location.pathname}/v/${record.id}`;

                              return (
                                <div key={key} style={style}>
                                  {cellRenderer({
                                    record,
                                    index,
                                    isScrolling,
                                    recordPath,
                                  })}
                                </div>
                              );
                            }}
                          />

                          {children({
                            height,
                            width,
                            scrollTop,
                            isScrolling,
                            boxes,
                            indicesRendered: this.indicesRendered,
                          })}

                          {loadMore && (
                            <PhotoGridScrollLoader
                              loadMore={loadMore}
                              shouldLoadMore={shouldLoadMore}
                              onMoreLoaded={this.onGridResize}
                              recordsLength={records.length}
                              indicesRendered={this.indicesRendered}
                            />
                          )}
                        </div>
                      );
                    }}
                  </AutoSizer>
                );
              }}
            </WindowScroller>
          )}
        </PhotoGridScrollContext.Consumer>
      </Fragment>
    );
  }
}

export default withRouter(PhotoGrid);
